import PropTypes from 'prop-types';
import { useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  TextField,
  makeStyles,
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core';
import TextFieldEmail from 'components/common/formik/TextFieldEmail';
import { selectedFspIdSelector } from 'selectors/fsp';
import { postApi } from 'util/api';

const CACHE_DURATION = 5 * 60 * 1000;
const useStyles = makeStyles((theme) => ({
  card: {
    maxWidth: 600,
    margin: '0 auto',
    marginTop: theme.spacing(4),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  actions: {
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const validate = (values) => {
  const errors = {};
  if (!values.loanNumber) {
    errors.loanNumber = 'Loan Number is required';
  } else if (!/^\d+$/.test(values.loanNumber)) {
    errors.loanNumber = 'Loan Number must contain only numbers';
  }
  return errors;
};

const PalGuestPaymentForm = ({ onSuccess }) => {
  const classes = useStyles();
  const fspId = useSelector(selectedFspIdSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const cacheRef = useRef({ data: null, timestamp: null, key: null });

  const generateCacheKey = useCallback((values, currentFspId) => {
    return JSON.stringify({
      loanNumber: values.loanNumber,
      accountSuffix: values.accountSuffix || '',
      email: values.email,
      fspId: currentFspId,
    });
  }, []);

  const isCacheValid = useCallback((cacheKey) => {
    const cache = cacheRef.current;
    if (!cache.data || !cache.timestamp || cache.key !== cacheKey) {
      return false;
    }
    const now = Date.now();
    return now - cache.timestamp < CACHE_DURATION;
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      setIsSubmitting(true);
      setError(null);

      const cacheKey = generateCacheKey(values, fspId);

      if (isCacheValid(cacheKey)) {
        onSuccess(cacheRef.current.data);
        setIsSubmitting(false);
        return;
      }

      try {
        const url = `/auth/fsps/${fspId}/loan-guest-verify`;
        const payload = {
          loanNumber: values.loanNumber,
          accountSuffix: values.accountSuffix || '',
          email: values.email,
        };

        const result = await postApi(url, payload);
        cacheRef.current = {
          data: result,
          timestamp: Date.now(),
          key: cacheKey,
        };

        onSuccess(result);
      } catch (err) {
        const errorMessage =
          err?.data?.message ||
          err?.message ||
          'Unable to verify loan. Please try again.';
        setError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [fspId, onSuccess, generateCacheKey, isCacheValid]
  );

  const initialValues = {
    loanNumber: '',
    accountSuffix: '',
    email: '',
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        title="PAL Guest Loan Payment"
        subheader="Enter loan information to verify and proceed with payment"
      />
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            <CardContent>
              <div className={classes.form}>
                <TextField
                  fullWidth
                  required
                  id="loanNumber"
                  name="loanNumber"
                  label="Loan Number"
                  value={values.loanNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.loanNumber && Boolean(errors.loanNumber)}
                  helperText={
                    touched.loanNumber && errors.loanNumber
                      ? errors.loanNumber
                      : 'Enter the loan account number'
                  }
                  disabled={isSubmitting}
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  id="accountSuffix"
                  name="accountSuffix"
                  label="Account Suffix"
                  value={values.accountSuffix}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountSuffix && Boolean(errors.accountSuffix)}
                  helperText={
                    touched.accountSuffix && errors.accountSuffix
                      ? errors.accountSuffix
                      : 'Optional: Enter the account suffix if applicable'
                  }
                  disabled={isSubmitting}
                  variant="outlined"
                />

                <TextFieldEmail
                  fullWidth
                  required
                  name="email"
                  label="Email"
                  disabled={isSubmitting}
                  variant="outlined"
                />

                {error && (
                  <Box mt={2}>
                    <Typography color="error" variant="body2">
                      {error}
                    </Typography>
                  </Box>
                )}
              </div>
            </CardContent>

            <CardActions className={classes.actions}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                size="large"
              >
                {isSubmitting ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress size={20} color="inherit" />
                    <span>Verifying...</span>
                  </div>
                ) : (
                  'Next'
                )}
              </Button>
            </CardActions>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

PalGuestPaymentForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

export default PalGuestPaymentForm;
